import React, { useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';

import Appearance from 'styles/Appearance.js';
import LottieView from 'views/Lottie.js';
import Utils from 'files/Utils.js';

const Button = ({ className, color, containerStyle, icon, label, loading, onClick, outline, style, transition, type = 'large' }) => {

    const buttonRef = useRef(null);

    const [hoverState, setHoverState] = useState(false);
    const [iconAnimations, setIconAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 20 },
        left: -350,
        opacity: 0
    }))
    const [labelAnimations, setLabelAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 20 },
        left: 0,
        opacity: 1
    }))

    const getStyles = () => {

        let styles = {
            backgroundColor: getColors(),
            borderRadius: 8,
            color: 'white',
            fontSize: 14,
            fontWeight: 800,
            height: 45,
            minWidth: 0,
            padding: '6px 18px 6px 18px',
            whiteSpace: 'normal',
            width: '100%',
            ...style
        };

        switch(type) {
            case 'transition':
            styles = {
                ...styles,
                fontSize: 13,
                fontWeight: 600,
                height: 45,
                padding: '8px 25px 8px 25px',
                width: '100%',
                ...style
            }
            break;

            case 'small':
            styles = {
                height: 19,
                fontSize: 10,
                padding: '3px 8px 3px 8px',
                ...style
            }
            break;
        }

        return styles;
    }

    const getColor = () => {
        switch(color) {
            case 'tertiary':
            return Appearance.colors.tertiary();

            case 'secondary':
            return Appearance.colors.secondary();

            case 'light':
            return Appearance.colors.lightGrey;

            case 'dark':
            return Appearance.colors.darkGrey;

            case 'grey':
            return Appearance.colors.grey();

            case 'danger':
            return Appearance.colors.red;

            case 'primary':
            return Appearance.colors.primary();

            case 'white':
            return '#FFFFFF';

            default:
            return color;
        }
    }

    const getColors = () => {
        if(hoverState) {
            switch(color) {
                case 'tertiary':
                return Utils.adjustColor(Appearance.colors.tertiary(), -35);

                case 'secondary':
                return Utils.adjustColor(Appearance.colors.secondary(), -35);

                case 'light':
                return Utils.adjustColor(Appearance.colors.lightGrey, -35);

                case 'dark':
                return Utils.adjustColor(Appearance.colors.darkGrey, -25);

                case 'grey':
                return Utils.adjustColor(Appearance.colors.grey(), -35); 

                case 'danger':
                return Utils.adjustColor(Appearance.colors.red, -35);

                case 'primary':
                return Utils.adjustColor(Appearance.colors.primary(), -35);

                case 'white':
                return Appearance.colors.divider();

                default:
                return Utils.adjustColor(color, -15);
            }
        }
        return getColor();
    }

    const getContent = () => {
        if(transition) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: 30,
                    opacity: loading === true ? 0 : 1,
                    position: 'relative',
                    textAlign: 'center',
                    width: '100%',
                }}>
                    <animated.div style={{
                        position: 'absolute',
                        width: '100%',
                        ...iconAnimations
                    }}>
                        <img
                        src={`images/${transition.icon}`}
                        style={{
                            width: 25,
                            height: 25,
                            objectFit: 'contain'
                        }} />
                    </animated.div>
                    <animated.div style={{
                        position: 'relative',
                        width: '100%',
                        ...labelAnimations
                    }}>
                        <span style={{
                            color: color === 'white' ? '#232323' : 'white',
                            display: 'block',
                            fontSize: 12,
                            fontFamily: 'Zen Dots',
                            maxWidth: '100%',
                            opacity: loading === true ? 0 : 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{label.toUpperCase()}</span>
                    </animated.div>
                </div>
            )
        }
        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                maxWidth: '100%'
            }}>
                {getIcon()}
                <span style={{
                    color: color === 'white' ? '#232323' : 'white',
                    display: 'block',
                    fontSize: 12,
                    fontFamily: 'Zen Dots',
                    maxWidth: '100%',
                    opacity: loading === true ? 0 : 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {label.toUpperCase()}
                </span>
            </div>
        )
    }

    const getIcon = () => {
        return typeof(icon) === 'string' && (
            <img 
            src={icon}
            style={{
                height: 25,
                marginRight: 8,
                objectFit: 'contain',
                width: 25
            }} />
        )
    }

    const onButtonClick = () => {
        onMouseLeave();
        if(typeof(onClick) === 'function') {
            onClick({ target: buttonRef.current });
        }
    }

    const onMouseEnter = () => {
        if(loading === true) {
            return;
        }
        setHoverState(true);
        if(type === 'transition') {
            setIconAnimations({
                left: 0,
                opacity: 1
            });
            setLabelAnimations({
                left: 250,
                opacity: 0
            });
        }
    }

    const onMouseLeave = () => {
        setHoverState(false);
        if(type === 'transition') {
            setIconAnimations({
                left: -350,
                opacity: 0
            });
            setLabelAnimations({
                left: 0,
                opacity: 1
            });
        }
    }

    return (
        <animated.div 
        className={className}
        style={{
            position: 'relative',
            ...containerStyle
        }}>
            <button
            className={'btn btn-pill'}
            onClick={loading === true ? null : onButtonClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            ref={buttonRef}
            style={{
                ...getStyles(),
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minWidth: 0,
                overflow: 'hidden',
                position: 'relative',
                ...color === 'white' && {
                    border: `2px solid ${Appearance.colors.divider()}`
                },
                ...outline === true && {
                    backgroundColor: hoverState ? 'rgba(255,255,255,0.25)' : Appearance.colors.transparent,
                    border: `2px solid ${getColor()}`
                },
                width: '100%'
            }}>
                {getContent()}
                {loading === true && (
                    <div style={{
                        position: 'absolute'
                    }}>
                        <LottieView
                        autoPlay={true}
                        loop={true}
                        source={require('files/lottie/dots-white.json')}
                        style={{
                            height: 25,
                            width: 25
                        }}/>
                    </div>
                )}
            </button>
        </animated.div>
    )
}
export default Button;
